import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  TrendUp, 
  TrendDown,
  Globe,
  Buildings,
  Plant,
  Cow,
  Flask,
  Tractor,
  Bank,
  ArrowSquareOut,
  Info,
  CaretDown,
  CaretRight,
  Spinner,
  ChartLine
} from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import StockPriceHistoryModal from "@/components/StockPriceHistoryModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Agriculture Stock Data from research
const STOCK_DATA = {
  uk: {
    name: "United Kingdom (LSE)",
    flag: "🇬🇧",
    description: "The UK market is strong in farm inputs and agricultural services, though large-scale farming companies are relatively rare.",
    stocks: [
      {
        ticker: "WYN",
        name: "Wynnstay Group plc",
        category: "Farm Inputs",
        description: "Feed, fertiliser, seed, and rural retail across UK",
        sector: "inputs",
        yield: "4-5%"
      },
      {
        ticker: "OGN",
        name: "Origin Enterprises plc",
        category: "Agronomy",
        description: "Agronomy, crop inputs, advisory services across UK, Ireland & Europe",
        sector: "inputs",
        yield: "3-4%"
      },
      {
        ticker: "NWF",
        name: "NWF Group plc",
        category: "Animal Feed",
        description: "Livestock feed manufacturing (NWF Agriculture division)",
        sector: "inputs",
        yield: "3-4%"
      },
      {
        ticker: "CARR",
        name: "Carr's Group plc",
        category: "Farm Inputs",
        description: "Feed blocks, supplements, and agricultural engineering",
        sector: "inputs",
        yield: "3-4%"
      },
      {
        ticker: "GNS",
        name: "Genus plc",
        category: "Livestock Genetics",
        description: "Animal genetics for dairy, beef, and pork production",
        sector: "livestock",
        yield: "1-2%"
      },
      {
        ticker: "CAM",
        name: "Camellia plc",
        category: "Plantations",
        description: "Tea, macadamia, avocado plantations worldwide",
        sector: "plantations",
        yield: "2-3%"
      },
      {
        ticker: "MPE",
        name: "M.P. Evans Group plc",
        category: "Plantations",
        description: "Palm oil plantations in Indonesia",
        sector: "plantations",
        yield: "2-3%"
      },
      {
        ticker: "CWK",
        name: "Cranswick plc",
        category: "Food Production",
        description: "Vertically integrated pig farming and food production",
        sector: "food",
        yield: "1-2%"
      },
      {
        ticker: "ANIC",
        name: "Agronomics Ltd",
        category: "Agri-Tech",
        description: "Cellular agriculture and lab-grown meat investments",
        sector: "agritech",
        yield: "0%"
      }
    ]
  },
  ireland: {
    name: "Ireland (Euronext Dublin)",
    flag: "🇮🇪",
    description: "Ireland gives high-quality, export-driven agriculture exposure, mostly post-farm-gate through processing and ingredients.",
    stocks: [
      {
        ticker: "OIZ",
        name: "Origin Enterprises plc",
        category: "Agronomy",
        description: "The standout Irish agri stock - agronomy, fertiliser, seed, advisory",
        sector: "inputs",
        yield: "3-4%",
        featured: true
      },
      {
        ticker: "KRZ",
        name: "Kerry Group plc",
        category: "Food Ingredients",
        description: "One of the world's largest food ingredients companies with strong dairy origins",
        sector: "food",
        yield: "1-2%"
      },
      {
        ticker: "GL9",
        name: "Glanbia plc",
        category: "Dairy & Nutrition",
        description: "Dairy, nutrition, protein - direct link to Irish milk production",
        sector: "dairy",
        yield: "2-3%"
      },
      {
        ticker: "FBD",
        name: "FBD Holdings plc",
        category: "Farm Insurance",
        description: "Ireland's leading insurer to farmers - proxy play on farm economy health",
        sector: "services",
        yield: "5-6%",
        featured: true
      }
    ]
  },
  global: {
    name: "Global Markets (NYSE/TSX)",
    flag: "🌍",
    description: "Global markets provide complete agricultural ecosystem exposure with greater scale, liquidity, and institutional participation.",
    stocks: [
      {
        ticker: "DE",
        name: "Deere & Company",
        category: "Machinery",
        description: "World's leading agricultural machinery manufacturer",
        sector: "machinery",
        yield: "1-2%",
        exchange: "NYSE"
      },
      {
        ticker: "NTR",
        name: "Nutrien Ltd",
        category: "Fertiliser",
        description: "Global fertiliser giant with strong pricing power in cycles",
        sector: "inputs",
        yield: "3-4%",
        exchange: "TSX/NYSE",
        featured: true
      },
      {
        ticker: "CF",
        name: "CF Industries Holdings",
        category: "Fertiliser",
        description: "Major nitrogen fertiliser producer, very profitable in upcycles",
        sector: "inputs",
        yield: "2-3%",
        exchange: "NYSE"
      },
      {
        ticker: "MOS",
        name: "The Mosaic Company",
        category: "Fertiliser",
        description: "Phosphate and potash producer for global crop nutrition",
        sector: "inputs",
        yield: "2-3%",
        exchange: "NYSE"
      },
      {
        ticker: "ADM",
        name: "Archer-Daniels-Midland",
        category: "Agribusiness",
        description: "Global grain trading and processing - dividend aristocrat (50+ years)",
        sector: "trading",
        yield: "2-3%",
        exchange: "NYSE"
      },
      {
        ticker: "BG",
        name: "Bunge Limited",
        category: "Agribusiness",
        description: "Global agribusiness and food company, leveraged to commodity flows",
        sector: "trading",
        yield: "2-3%",
        exchange: "NYSE",
        featured: true
      },
      {
        ticker: "CTVA",
        name: "Corteva Inc",
        category: "Crop Science",
        description: "Seeds and crop protection products for global agriculture",
        sector: "inputs",
        yield: "1-2%",
        exchange: "NYSE"
      },
      {
        ticker: "AGCO",
        name: "AGCO Corporation",
        category: "Machinery",
        description: "Agricultural equipment manufacturer (Massey Ferguson, Fendt)",
        sector: "machinery",
        yield: "1-2%",
        exchange: "NYSE"
      },
      {
        ticker: "TSN",
        name: "Tyson Foods Inc",
        category: "Protein",
        description: "One of the world's largest protein companies (beef, pork, chicken)",
        sector: "food",
        yield: "3-4%",
        exchange: "NYSE"
      }
    ]
  }
};

// Sector Icons
const SECTOR_ICONS = {
  inputs: Plant,
  livestock: Cow,
  plantations: Plant,
  food: Buildings,
  dairy: Cow,
  agritech: Flask,
  machinery: Tractor,
  trading: Globe,
  services: Bank
};

// Sector Colors
const SECTOR_COLORS = {
  inputs: "bg-[#ECFDF5] text-[#059669] border-[#A7F3D0]",
  livestock: "bg-[#FEF3C7] text-[#D97706] border-[#FDE68A]",
  plantations: "bg-[#F0FDF4] text-[#15803D] border-[#86EFAC]",
  food: "bg-[#EFF6FF] text-[#2563EB] border-[#BFDBFE]",
  dairy: "bg-[#F0FDFA] text-[#0F766E] border-[#99F6E4]",
  agritech: "bg-[#F5F3FF] text-[#7C3AED] border-[#DDD6FE]",
  machinery: "bg-[#FFF7ED] text-[#EA580C] border-[#FED7AA]",
  trading: "bg-[#F8FAFC] text-[#475569] border-[#CBD5E1]",
  services: "bg-[#FDF2F8] text-[#DB2777] border-[#FBCFE8]"
};

function StockCard({ stock, livePrice, onChartClick }) {
  const SectorIcon = SECTOR_ICONS[stock.sector] || Buildings;
  const sectorColor = SECTOR_COLORS[stock.sector] || SECTOR_COLORS.food;

  return (
    <div 
      className={`bg-white border ${stock.featured ? 'border-[#0F766E] ring-1 ring-[#0F766E]/20' : 'border-[#E0D4C8]'} p-5 hover:border-[#0F766E] transition-all group`}
      data-testid={`stock-card-${stock.ticker}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-lg font-bold text-[#33302E]">{stock.ticker}</span>
            {stock.featured && (
              <span className="px-1.5 py-0.5 bg-[#0F766E] text-white text-[9px] font-bold uppercase tracking-wider">
                Top Pick
              </span>
            )}
          </div>
          <h3 className="font-serif text-base text-[#374151] leading-snug">{stock.name}</h3>
        </div>
        <div className={`p-2 rounded ${sectorColor.split(' ')[0]}`}>
          <SectorIcon size={20} className={sectorColor.split(' ')[1]} />
        </div>
      </div>
      
      {/* Live Price Display */}
      {livePrice && (
        <button
          onClick={() => onChartClick(stock, livePrice)}
          className="w-full flex items-center justify-between mb-3 p-2 bg-[#F9FAFB] rounded hover:bg-[#F0FDF4] transition-colors cursor-pointer group/price"
          data-testid={`stock-price-${stock.ticker}`}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6B6661] font-mono">{livePrice.currency}</span>
            <span className="font-mono text-lg font-bold text-[#33302E]">
              {livePrice.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1 ${livePrice.change_percent >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
              {livePrice.change_percent >= 0 ? <TrendUp size={16} /> : <TrendDown size={16} />}
              <span className="font-mono text-sm font-medium">
                {livePrice.change_percent >= 0 ? '+' : ''}{livePrice.change_percent.toFixed(2)}%
              </span>
            </div>
            <ChartLine size={16} className="text-[#6B6661] group-hover/price:text-[#0F766E] transition-colors" />
          </div>
        </button>
      )}
      
      <p className="text-sm text-[#6B6661] mb-3 line-clamp-2">{stock.description}</p>
      
      <div className="flex items-center justify-between pt-3 border-t border-[#F3F4F6]">
        <span className={`px-2 py-0.5 text-xs font-medium border rounded ${sectorColor}`}>
          {stock.category}
        </span>
        {stock.yield && (
          <span className="text-xs text-[#059669] font-medium">
            Yield: {stock.yield}
          </span>
        )}
      </div>
    </div>
  );
}

function MarketSection({ marketKey, market, isExpanded, onToggle, livePrices, onChartClick }) {
  return (
    <div className="mb-8">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-[#022C22] to-[#064E3B] text-white hover:from-[#064E3B] hover:to-[#0F766E] transition-colors mb-4"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? <CaretDown size={20} /> : <CaretRight size={20} />}
          <span className="text-2xl">{market.flag}</span>
          <div className="text-left">
            <h2 className="font-serif text-xl">{market.name}</h2>
            <p className="text-sm text-[#A7F3D0]">{market.stocks.length} companies</p>
          </div>
        </div>
      </button>
      
      {isExpanded && (
        <>
          <p className="text-[#6B6661] mb-6 px-1">{market.description}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {market.stocks.map((stock) => (
              <StockCard 
                key={stock.ticker} 
                stock={stock} 
                livePrice={livePrices[stock.ticker]}
                onChartClick={onChartClick}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AgriStocksPage() {
  const [expandedMarkets, setExpandedMarkets] = useState({
    uk: true,
    ireland: true,
    global: true
  });
  const [livePrices, setLivePrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);

  useEffect(() => {
    fetchLivePrices();
    // Refresh prices every 5 minutes
    const interval = setInterval(fetchLivePrices, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchLivePrices = async () => {
    try {
      const response = await axios.get(`${API}/agri-stocks/prices`);
      setLivePrices(response.data.prices || {});
      setLastUpdated(response.data.last_updated);
    } catch (error) {
      console.error("Error fetching stock prices:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMarket = (key) => {
    setExpandedMarkets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChartClick = (stock, livePrice) => {
    setSelectedStock({
      ticker: stock.ticker,
      name: stock.name,
      price: livePrice.price,
      changePercent: livePrice.change_percent,
      currency: livePrice.currency
    });
  };

  const closeModal = () => {
    setSelectedStock(null);
  };

  // Calculate totals
  const totalStocks = Object.values(STOCK_DATA).reduce((acc, m) => acc + m.stocks.length, 0);
  const featuredStocks = Object.values(STOCK_DATA).reduce(
    (acc, m) => acc + m.stocks.filter(s => s.featured).length, 0
  );

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEOHead 
        title="Agriculture Stocks Watchlist"
        url="/agri-stocks"
        description="Comprehensive list of agriculture and farming companies listed on LSE, Irish Stock Exchange, and global markets. Track farm inputs, machinery, food producers, and agribusiness stocks."
        keywords="agriculture stocks, farming companies, LSE agriculture, Irish agri stocks, farm investment, Wynnstay, Origin Enterprises, Deere, Nutrien"
      />
      <Header />
      
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        {/* Hero Section */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <TrendUp size={32} className="text-[#0F766E]" weight="duotone" />
            <h1 className="font-serif text-3xl sm:text-4xl text-[#33302E]">
              Agriculture Stocks Watchlist
            </h1>
          </div>
          <p className="text-[#6B6661] max-w-3xl mb-6">
            A comprehensive reference list of agriculture and farming-related companies listed on major stock exchanges. 
            This includes farm inputs, machinery, food producers, and agribusiness companies across UK, Ireland, and global markets.
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E0D4C8]">
              <Buildings size={20} className="text-[#0F766E]" />
              <span className="text-sm text-[#374151]">
                <strong>{totalStocks}</strong> Companies
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E0D4C8]">
              <Globe size={20} className="text-[#0F766E]" />
              <span className="text-sm text-[#374151]">
                <strong>3</strong> Markets
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E0D4C8]">
              <TrendUp size={20} className="text-[#059669]" />
              <span className="text-sm text-[#374151]">
                <strong>{featuredStocks}</strong> Top Picks
              </span>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="flex items-start gap-3 p-4 bg-[#FEF3C7] border border-[#FDE68A] text-sm">
            <Info size={20} className="text-[#D97706] flex-shrink-0 mt-0.5" />
            <p className="text-[#92400E]">
              <strong>Disclaimer:</strong> This is a reference list only, not investment advice. 
              Stock prices, yields, and company details change frequently. Always conduct your own research 
              and consult a qualified financial advisor before making investment decisions.
            </p>
          </div>
        </div>

        {/* Sector Legend */}
        <div className="mb-8 p-4 bg-white border border-[#E0D4C8]">
          <h3 className="font-semibold text-[#33302E] mb-3">Sector Guide</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(SECTOR_COLORS).map(([sector, color]) => {
              const Icon = SECTOR_ICONS[sector];
              return (
                <span key={sector} className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs border rounded ${color}`}>
                  <Icon size={14} />
                  {sector.charAt(0).toUpperCase() + sector.slice(1)}
                </span>
              );
            })}
          </div>
        </div>

        {/* Live Prices Status */}
        {!loading && lastUpdated && (
          <div className="mb-4 flex items-center justify-between px-4 py-2 bg-[#F0FDF4] border border-[#A7F3D0] rounded">
            <span className="text-sm text-[#059669] font-medium">
              Live prices from {Object.keys(livePrices).length} stocks
            </span>
            <span className="text-xs text-[#6B6661]">
              Updated: {new Date(lastUpdated).toLocaleTimeString()}
            </span>
          </div>
        )}

        {/* Market Sections */}
        {Object.entries(STOCK_DATA).map(([key, market]) => (
          <MarketSection
            key={key}
            marketKey={key}
            market={market}
            isExpanded={expandedMarkets[key]}
            onToggle={() => toggleMarket(key)}
            livePrices={livePrices}
            onChartClick={handleChartClick}
          />
        ))}

        {/* Investment Framework */}
        <div className="mt-12 bg-white border border-[#E0D4C8] p-8">
          <h2 className="font-serif text-2xl text-[#33302E] mb-6">Understanding Agriculture Investment</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-[#33302E] mb-3">The Four Layers</h3>
              <p className="text-sm text-[#6B6661] mb-4">
                Agriculture investing spans multiple layers of the value chain:
              </p>
              <ul className="space-y-2 text-sm text-[#6B6661]">
                <li className="flex items-start gap-2">
                  <Plant size={16} className="text-[#0F766E] mt-0.5" />
                  <span><strong>Inputs</strong> — Fertiliser, seed, agronomy (40% allocation)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Tractor size={16} className="text-[#0F766E] mt-0.5" />
                  <span><strong>Machinery</strong> — Equipment manufacturers (30% allocation)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Buildings size={16} className="text-[#0F766E] mt-0.5" />
                  <span><strong>Food/Protein</strong> — Processors, producers (20% allocation)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Flask size={16} className="text-[#0F766E] mt-0.5" />
                  <span><strong>Agri-Tech</strong> — Future farming technology (10% allocation)</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-[#33302E] mb-3">Cycle Behaviour</h3>
              <p className="text-sm text-[#6B6661] mb-4">
                Agriculture runs in cycles linked to commodity prices and farm profitability:
              </p>
              <ul className="space-y-2 text-sm text-[#6B6661]">
                <li className="flex items-start gap-2">
                  <TrendDown size={16} className="text-[#DC2626] mt-0.5" />
                  <span><strong>Stage 1:</strong> Fertiliser prices decline first</span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendDown size={16} className="text-[#DC2626] mt-0.5" />
                  <span><strong>Stage 2:</strong> Farm profitability weakens</span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendDown size={16} className="text-[#DC2626] mt-0.5" />
                  <span><strong>Stage 3:</strong> Machinery demand falls</span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendUp size={16} className="text-[#059669] mt-0.5" />
                  <span><strong>Stage 4:</strong> Recovery begins with inputs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Link 
            to="/stock-news" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#064E3B] transition-colors"
          >
            View Stock News
            <ArrowSquareOut size={18} />
          </Link>
          <Link 
            to="/news" 
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#E0D4C8] text-[#374151] hover:border-[#0F766E] hover:text-[#0F766E] transition-colors"
          >
            Read Agriculture News
            <ArrowSquareOut size={18} />
          </Link>
          <Link 
            to="/content-calendar" 
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#E0D4C8] text-[#374151] hover:border-[#0F766E] hover:text-[#0F766E] transition-colors"
          >
            View Content Calendar
            <ArrowSquareOut size={18} />
          </Link>
        </div>
      </main>

      <Footer />

      {/* Stock Price History Modal */}
      <StockPriceHistoryModal
        isOpen={selectedStock !== null}
        onClose={closeModal}
        ticker={selectedStock?.ticker}
        stockName={selectedStock?.name}
        currentPrice={selectedStock?.price}
        changePercent={selectedStock?.changePercent}
        currency={selectedStock?.currency}
      />
    </div>
  );
}
